import { useEffect, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import { canOperateInventory } from "@/lib/permissions";
import { PageHeader } from "@/components/base/PageHeader";
import { ListSkeleton } from "@/components/base/ListSkeleton";
import { EmptyState } from "@/components/base/EmptyState";
import { Modal } from "@/components/base/Modal";
import Pagination from "@/components/base/Pagination";
import { fmtDateTime, variantLabel } from "@/lib/format";
import { resolveLedgerReferences, referenceKey } from "@/lib/references";
import * as inventoryApi from "@/lib/api/inventory";
import { downloadCsv, toCsv } from "@/lib/csv";
import { classifyStock, stockStatusLabel, wouldGoNegative, movementLabel, type StockFilter } from "@/lib/inventory";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import OpeningInventoryModal from "./components/OpeningInventoryModal";
import { firstRelation } from "@/lib/relations";
import { historicalProductName, type CatalogueHistoryFields } from "@/lib/catalogue-history";

interface BalanceRow {
  id: string;
  product_id: string;
  variant_id: string | null;
  location_id: string;
  on_hand: number;
  allocated: number;
  available: number;
  incoming: number;
  transferring: number;
  product_name: string | null;
  product_sku: string | null;
  variant_sku: string | null;
  variant_barcode: string | null;
  variant_options: Record<string, string> | null;
  location_name: string | null;
  reorder_point: number;
  total_count: number;
}

interface VariantOption { id: string; product_id: string; sku: string | null; barcode: string | null; option_values: Record<string, string> | null; }

interface ProductOption { id: string; name: string; sku: string; reorder_point: number; }
interface LocationOption { id: string; name: string; code: string | null; }

interface LedgerRow extends CatalogueHistoryFields {
  id: string;
  entry_type: string;
  qty_change: number;
  qty_before: number | null;
  qty_after: number | null;
  reference_type: string | null;
  reference_id: string | null;
  created_at: string;
  products?: { name: string } | null;
  locations?: { name: string } | null;
}

const inputCls =
  "w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500";
const labelCls = "block text-xs font-medium text-slate-500 mb-1";

const outlineBtn =
  "px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-lg transition-colors whitespace-nowrap flex items-center gap-2";

export default function Inventory() {
  const { org, role } = useAuth();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search, 300);
  const [locationFilter, setLocationFilter] = useState("");
  const [stockFilter, setStockFilter] = useState<StockFilter>("all");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ product_id: "", variant_id: "", location_id: "", qty: "", reason: "" });
  const [openingOpen, setOpeningOpen] = useState(false);
  const [adjustIdemKey, setAdjustIdemKey] = useState("");
  const [bulkSelected, setBulkSelected] = useState<Set<string>>(new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkQty, setBulkQty] = useState("");
  const [bulkReason, setBulkReason] = useState("");
  const [bulkSaving, setBulkSaving] = useState(false);
  const [bulkError, setBulkError] = useState("");
  const [bulkResult, setBulkResult] = useState("");

  const { data, isError, refetch } = useQuery({
    queryKey: ["inventory", org?.id, debouncedSearch, locationFilter, stockFilter, page, pageSize],
    enabled: !!org?.id,
    queryFn: async () => {
      const [pageRes, products, locations, ledger, variants] = await Promise.all([
        inventoryApi.getInventoryPage<BalanceRow>({
          orgId: org!.id,
          search: debouncedSearch || null,
          locationId: locationFilter || null,
          stockFilter,
          page,
          pageSize,
        }),
        supabase.from("products").select("id, name, sku, reorder_point").eq("org_id", org!.id).order("name"),
        supabase.from("locations").select("id, name, code").eq("org_id", org!.id).order("name"),
        supabase.from("inventory_ledger").select("product_id, variant_id, catalogue_product_identity_id, catalogue_variant_identity_id, product_name_snapshot, sku_snapshot, catalogue_snapshot_origin, id, entry_type, qty_change, qty_before, qty_after, reference_type, reference_id, created_at, products(name), locations(name)").eq("org_id", org!.id).order("created_at", { ascending: false }).limit(30),
        supabase.from("variants").select("id, product_id, sku, barcode, option_values").eq("org_id", org!.id).order("position"),
      ]);
      const rows = pageRes ?? [];
      const total = rows.length > 0 ? rows[0].total_count : 0;
      const ledgerRows = (ledger.data ?? []).map((row) => ({
        ...row,
        products: firstRelation(row.products),
        locations: firstRelation(row.locations),
      })) as LedgerRow[];
      const references = await resolveLedgerReferences(org!.id, ledgerRows);
      return {
        rows,
        total,
        products: (products.data ?? []) as ProductOption[],
        locations: (locations.data ?? []).map((row) => ({ ...row, code: row.code ?? null })) as LocationOption[],
        ledger: ledgerRows,
        references,
        variants: (variants.data ?? []) as VariantOption[],
      };
    },
  });

  const selectedProductVariants = (data?.variants ?? []).filter((v) => v.product_id === form.product_id);

  // Page-scoped bulk selection — operating on a single page keeps it safe and predictable.
  const selectedRows = (data?.rows ?? []).filter((r) => bulkSelected.has(r.id));
  const allPageSelected = (data?.rows?.length ?? 0) > 0 && selectedRows.length === (data?.rows?.length ?? 0);

  const toggleBulk = (id: string) => {
    setBulkSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleBulkAll = () => {
    if (allPageSelected) setBulkSelected(new Set());
    else setBulkSelected(new Set((data?.rows ?? []).map((r) => r.id)));
  };

  // On-demand lookup for the "currently on hand" hint in the adjustment modal.
  // Independent of the paginated list, so the hint is accurate for any balance.
  const [selectedBalance, setSelectedBalance] = useState<BalanceRow | null>(null);
  useEffect(() => {
    if (!org?.id || !form.product_id || !form.location_id || !open) {
      setSelectedBalance(null);
      return;
    }
    let cancelled = false;
    let q = supabase
      .from("inventory_balances")
      .select("id, product_id, variant_id, location_id, on_hand, allocated, available, incoming, transferring")
      .eq("org_id", org.id)
      .eq("product_id", form.product_id)
      .eq("location_id", form.location_id);
    q = form.variant_id ? q.eq("variant_id", form.variant_id) : q.is("variant_id", null);
    q.maybeSingle().then(({ data: row }) => {
      if (!cancelled) setSelectedBalance((row as BalanceRow | null) ?? null);
    });
    return () => {
      cancelled = true;
    };
  }, [org?.id, form.product_id, form.variant_id, form.location_id, open]);

  const handleAdjust = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    const qty = Number(form.qty);
    const hasVariants = selectedProductVariants.length > 0;
    if (!form.product_id || !form.location_id || !form.qty || Number.isNaN(qty) || qty === 0) {
      setError("Select a product, location, and a non-zero quantity.");
      return;
    }
    if (hasVariants && !form.variant_id) {
      setError("Select a variant for this product.");
      return;
    }
    setSaving(true);
    try {
      await inventoryApi.applyInventoryAdjustment({
        productId: form.product_id,
        locationId: form.location_id,
        qtyChange: qty,
        reason: form.reason || null,
        variantId: form.variant_id || null,
        idempotencyKey: adjustIdemKey || undefined,
      });
    } catch (e) {
      setSaving(false);
      setError((e as Error).message);
      return;
    }
    setSaving(false);
    setOpen(false);
    setForm({ product_id: "", variant_id: "", location_id: "", qty: "", reason: "" });
    queryClient.invalidateQueries({ queryKey: ["inventory", org?.id] });
    queryClient.invalidateQueries({ queryKey: ["dashboard", org?.id] });
  };

  const handleBulkAdjust = async () => {
    setBulkError("");
    setBulkResult("");
    const qty = Number(bulkQty);
    if (!bulkQty || Number.isNaN(qty) || qty === 0) {
      setBulkError("Enter a non-zero quantity change.");
      return;
    }
    if (selectedRows.length === 0) {
      setBulkError("No rows selected.");
      return;
    }
    setBulkSaving(true);
    let applied = 0;
    let skipped = 0;
    let failed = 0;
    for (const b of selectedRows) {
      // Refuse reductions that would drive a location below zero.
      if (qty < 0 && wouldGoNegative(b.on_hand, qty)) {
        skipped += 1;
        continue;
      }
      try {
        await inventoryApi.applyInventoryAdjustment({
          productId: b.product_id,
          locationId: b.location_id,
          qtyChange: qty,
          reason: bulkReason || null,
          variantId: b.variant_id || null,
          idempotencyKey: crypto.randomUUID(),
        });
        applied += 1;
      } catch {
        failed += 1;
      }
    }
    setBulkSaving(false);
    const parts = [`Applied ${applied}`];
    if (skipped) parts.push(`skipped ${skipped} (would go below zero)`);
    if (failed) parts.push(`${failed} failed`);
    setBulkResult(`${parts.join(", ")}.`);
    setBulkSelected(new Set());
    setBulkQty("");
    setBulkReason("");
    queryClient.invalidateQueries({ queryKey: ["inventory", org?.id] });
    queryClient.invalidateQueries({ queryKey: ["dashboard", org?.id] });
  };

  const handleExport = () => {
    if (!data) return;
    const headers = ["product", "sku", "variant", "location", "on_hand", "available", "allocated", "incoming", "reorder_point", "transferring"];
    const rows = data.rows.map((b) => [
      b.product_name ?? "",
      b.product_sku ?? "",
      (variantLabel(b.variant_options) || b.variant_sku || ""),
      b.location_name ?? "",
      b.on_hand, b.available, b.allocated, b.incoming, b.reorder_point, b.transferring,
    ]);
    downloadCsv(`inventory-${new Date().toISOString().slice(0, 10)}.csv`, toCsv(headers, rows));
  };

  // isLoading is false when the query is disabled (org not ready yet) or when
  // a fetch has not started. Rendering `data.rows` in that window throws and
  // the error boundary replaces the whole screen — RC-003B/C Inventory.
  if (!data) {
    if (isError) {
      return (
        <div>
          <PageHeader title="Inventory" subtitle="Track stock levels and movements across all locations" />
          <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
            <p className="text-sm text-slate-500 mb-4">Couldn't load inventory.</p>
            <button onClick={() => refetch()} className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium rounded-lg whitespace-nowrap">Retry</button>
          </div>
        </div>
      );
    }
    return <ListSkeleton />;
  }

  return (
    <div>
      <PageHeader title="Inventory" subtitle="Track stock levels and movements across all locations">
        {canOperateInventory(role) && (
          <Link to="/stocktake" className={outlineBtn}>
            <i className="ri-clipboard-line" /> Stocktake
          </Link>
        )}
        {canOperateInventory(role) && (
          <Link to="/transfers" className={outlineBtn}>
            <i className="ri-arrow-left-right-line" /> Transfer stock
          </Link>
        )}
        {canOperateInventory(role) && (
          <Link to="/replenishment" className={outlineBtn}>
            <i className="ri-shopping-cart-line" /> Replenish
          </Link>
        )}
        <button onClick={handleExport} className={outlineBtn}>
          <i className="ri-download-2-line" /> Export
        </button>
        {canOperateInventory(role) && (
          <button onClick={() => setOpeningOpen(true)} className={outlineBtn}>
            <i className="ri-upload-2-line" /> Import opening stock
          </button>
        )}
        {canOperateInventory(role) && (
          <button onClick={() => { setAdjustIdemKey(crypto.randomUUID()); setOpen(true); }} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-lg transition-colors whitespace-nowrap flex items-center gap-2">
            <i className="ri-add-line" />
            Adjust Stock
          </button>
        )}
      </PageHeader>

      {isError ? (
        <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
          <p className="text-sm text-slate-500 mb-4">Couldn't load inventory.</p>
          <button onClick={() => refetch()} className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium rounded-lg whitespace-nowrap">Retry</button>
        </div>
      ) : (
        <>
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden mb-6">
            <div className="p-4 border-b border-slate-100 flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1 max-w-md">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                  aria-label="Search inventory by name or SKU"
                  placeholder="Search by name, SKU or barcode..."
                  className="w-full pl-9 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                />
              </div>
              <select value={locationFilter} onChange={(e) => { setLocationFilter(e.target.value); setPage(0); }} aria-label="Filter by location" className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm">
                <option value="">All locations</option>
                {data?.locations.map((l) => (
                  <option key={l.id} value={l.id}>{l.name}</option>
                ))}
              </select>
              <select value={stockFilter} onChange={(e) => { setStockFilter(e.target.value as StockFilter); setPage(0); }} aria-label="Filter by stock status" className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm">
                <option value="all">All stock</option>
                <option value="out">Out of stock</option>
                <option value="low">Low stock</option>
                <option value="incoming">Has incoming</option>
                <option value="allocated">Has allocated</option>
                <option value="available">Available</option>
              </select>
            </div>

            <div className="px-4 py-2.5 bg-slate-50/60 border-b border-slate-100 flex items-center gap-x-5 gap-y-1 flex-wrap text-xs text-slate-500">
              <span className="flex items-center gap-1"><i className="ri-information-line text-slate-400" />Available = On hand − Allocated</span>
              <span className="flex items-center gap-1"><i className="ri-reserved-line text-slate-400" />Allocated is reserved for open orders</span>
              <span className="flex items-center gap-1"><i className="ri-truck-line text-slate-400" />Incoming is due on purchase orders</span>
              <span className="flex items-center gap-1"><i className="ri-notification-3-line text-slate-400" />Reorder is when to order more</span>
              <span className="flex items-center gap-1"><i className="ri-arrow-left-right-line text-slate-400" />Transferring is in transit out</span>
            </div>

            {bulkSelected.size > 0 && (
              <div className="px-4 py-2.5 bg-emerald-50 border-b border-emerald-100 flex items-center gap-3 flex-wrap">
                <span className="text-sm text-emerald-800 font-medium">{selectedRows.length} selected</span>
                {canOperateInventory(role) && (
                  <button onClick={() => { setBulkError(""); setBulkResult(""); setBulkQty(""); setBulkReason(""); setBulkOpen(true); }} className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium rounded-md whitespace-nowrap flex items-center gap-1.5">
                    <i className="ri-add-line" /> Adjust selected
                  </button>
                )}
                <button onClick={() => setBulkSelected(new Set())} className="px-3 py-1.5 bg-white border border-slate-200 hover:bg-slate-50 text-slate-600 text-xs font-medium rounded-md whitespace-nowrap">
                  Clear
                </button>
              </div>
            )}

            {!data || data.rows.length === 0 ? (
              search || locationFilter || stockFilter !== "all" ? (
                <EmptyState
                  title="No stock matches"
                  description="Nothing matches your current search or filters. Try clearing them."
                />
              ) : (
                <EmptyState
                  title="No stock records yet"
                  description="Stock appears here once you import opening stock or receive a purchase order."
                  action={canOperateInventory(role) ? (
                    <button onClick={() => setOpeningOpen(true)} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-lg whitespace-nowrap flex items-center gap-2">
                      <i className="ri-upload-2-line" /> Import opening stock
                    </button>
                  ) : undefined}
                />
              )
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-slate-50/70 border-b border-slate-100">
                      <th className="w-10 px-4 py-3">
                        <input
                          type="checkbox"
                          checked={allPageSelected}
                          onChange={toggleBulkAll}
                          className="w-4 h-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500 cursor-pointer"
                          aria-label="Select all on this page"
                        />
                      </th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Product</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Location</th>
                      <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider" title="Physical units currently in this location">On hand</th>
                      <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider" title="On hand minus allocated — what you can still sell or move">Available</th>
                      <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider" title="Reserved against open sales orders">Allocated</th>
                      <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider" title="Expected from open purchase orders">Incoming</th>
                      <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider" title="Reorder point — order more when available drops to or below this">Reorder</th>
                      <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider" title="In transit out of this location on a stock transfer">Transferring</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.rows.map((b) => {
                      const status = classifyStock(b.on_hand, b.available, b.reorder_point);
                      const sku = b.variant_sku ?? b.product_sku ?? "—";
                      return (
                        <tr key={b.id} className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors">
                          <td className="px-4 py-3">
                            <input
                              type="checkbox"
                              checked={bulkSelected.has(b.id)}
                              onChange={() => toggleBulk(b.id)}
                              className="w-4 h-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500 cursor-pointer"
                              aria-label={`Select ${b.product_name ?? sku}`}
                            />
                          </td>
                          <td className="px-4 py-3">
                            <Link to={`/products/${b.product_id}`} className="text-sm font-medium text-slate-800 hover:text-emerald-700 hover:underline">
                              {b.product_name ?? "—"}
                              {variantLabel(b.variant_options) && (
                                <span className="text-slate-500 font-normal ml-1">· {variantLabel(b.variant_options)}</span>
                              )}
                            </Link>
                            <p className="text-xs text-slate-400 font-mono">{sku}</p>
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-600">
                            <Link to={`/locations/${b.location_id}`} className="hover:text-emerald-700 hover:underline">{b.location_name ?? "—"}</Link>
                          </td>
                          <td className={`px-4 py-3 text-sm text-right font-medium ${b.on_hand <= 0 ? "text-red-600" : "text-slate-800"}`}>{b.on_hand}</td>
                          <td className="px-4 py-3 text-sm text-right font-semibold">
                            <span className={status === "out" ? "text-red-600" : status === "low" ? "text-amber-600" : "text-emerald-700"}>{b.available}</span>
                            {status !== "ok" && (
                              <span className={`block text-[10px] font-medium ${status === "out" ? "text-red-600" : "text-amber-600"}`}>{stockStatusLabel(status)}</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-600 text-right">{b.allocated}</td>
                          <td className="px-4 py-3 text-sm text-slate-600 text-right">{b.incoming}</td>
                          <td className="px-4 py-3 text-right">
                            {b.reorder_point > 0 ? (
                              <span className={`text-sm font-medium ${status === "out" ? "text-red-600" : status === "low" ? "text-amber-600" : "text-slate-500"}`}>
                                {b.reorder_point}
                              </span>
                            ) : (
                              <span className="text-xs text-slate-300">—</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-500 text-right font-medium">{b.transferring}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            <Pagination page={page} pageSize={pageSize} total={data?.total ?? 0} onPageChange={setPage} onPageSizeChange={(s) => { setPageSize(s); setPage(0); }} />
          </div>

          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="p-4 border-b border-slate-100 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-slate-800">Recent Stock Movements</h3>
              <span className="text-xs text-slate-400">Latest {data?.ledger.length ?? 0} entries</span>
            </div>
            {!data || data.ledger.length === 0 ? (
              <div className="px-4 py-6 text-center text-sm text-slate-400">No movements recorded yet</div>
            ) : (
              <div className="divide-y divide-slate-50">
                {data.ledger.map((m) => {
                  const refKey = referenceKey(m.reference_type, m.reference_id);
                  const ref = refKey ? data.references.get(refKey) : undefined;
                  return (
                    <div key={m.id} className="px-4 py-3 flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm text-slate-800">
                          <span className="font-medium">{movementLabel(m.entry_type, m.reference_type)}</span>
                          <span className="text-slate-400"> · {historicalProductName(m)}</span>
                        </p>
                        <p className="text-xs text-slate-400 mt-0.5">
                          {m.locations?.name ?? "—"} · {fmtDateTime(m.created_at)}
                          {ref && (
                            <>
                              {" · "}
                              {ref.route ? (
                                <Link to={ref.route} className="text-emerald-600 hover:text-emerald-700 hover:underline font-medium">
                                  {ref.label}
                                </Link>
                              ) : (
                                <span className="text-slate-500">{ref.label}</span>
                              )}
                            </>
                          )}
                        </p>
                      </div>
                      <span className={`text-sm font-semibold whitespace-nowrap ${m.qty_change >= 0 ? "text-emerald-700" : "text-red-600"}`}>
                        {m.qty_change >= 0 ? "+" : ""}{m.qty_change}
                        <span className="text-slate-400 font-normal text-xs ml-1">({m.qty_before} → {m.qty_after})</span>
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title="Adjust Stock"
        footer={
          <>
            <button onClick={() => setOpen(false)} className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-lg whitespace-nowrap">Cancel</button>
            <button onClick={handleAdjust} disabled={saving} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-sm font-medium rounded-lg whitespace-nowrap">
              {saving ? "Applying..." : "Apply Adjustment"}
            </button>
          </>
        }
      >
        <form onSubmit={handleAdjust} className="space-y-4">
          {error && <div className="px-3 py-2.5 bg-red-50 border border-red-100 rounded-lg text-sm text-red-700">{error}</div>}
          <div>
            <label className={labelCls}>Product *</label>
            <select required value={form.product_id} onChange={(e) => setForm((f) => ({ ...f, product_id: e.target.value, variant_id: "" }))} className={inputCls}>
              <option value="">Select product...</option>
              {data?.products.map((p) => (
                <option key={p.id} value={p.id}>{p.name} ({p.sku})</option>
              ))}
            </select>
          </div>
          {selectedProductVariants.length > 0 && (
            <div>
              <label className={labelCls}>Variant *</label>
              <select required value={form.variant_id} onChange={(e) => setForm((f) => ({ ...f, variant_id: e.target.value }))} className={inputCls}>
                <option value="">Select variant...</option>
                {selectedProductVariants.map((v) => (
                  <option key={v.id} value={v.id}>{variantLabel(v.option_values) || v.sku}</option>
                ))}
              </select>
            </div>
          )}
          <div>
            <label className={labelCls}>Location *</label>
            <select required value={form.location_id} onChange={(e) => setForm((f) => ({ ...f, location_id: e.target.value }))} className={inputCls}>
              <option value="">Select location...</option>
              {data?.locations.map((l) => (
                <option key={l.id} value={l.id}>{l.name}</option>
              ))}
            </select>
            {selectedBalance && (
              <p className="text-xs text-slate-500 mt-1.5">
                Currently <span className="font-semibold text-slate-800">{selectedBalance.on_hand} on hand</span>
                {" · "}
                <span className="font-semibold text-emerald-700">{selectedBalance.available} available</span>
              </p>
            )}
          </div>
          <div>
            <label className={labelCls}>Quantity change *</label>
            <input type="number" required value={form.qty} onChange={(e) => setForm((f) => ({ ...f, qty: e.target.value }))} className={inputCls} placeholder="e.g. 10 or -5" />
            <p className="text-xs text-slate-400 mt-1">Use a negative number to reduce stock.</p>
          </div>
          <div>
            <label className={labelCls}>Reason</label>
            <input value={form.reason} onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))} className={inputCls} placeholder="Cycle count, damage, etc." />
          </div>
        </form>
      </Modal>

      <Modal open={bulkOpen} onClose={() => setBulkOpen(false)} title={`Adjust ${selectedRows.length} stock line${selectedRows.length === 1 ? "" : "s"}`}
        footer={
          <>
            <button onClick={() => setBulkOpen(false)} className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-lg whitespace-nowrap">Cancel</button>
            <button onClick={handleBulkAdjust} disabled={bulkSaving} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-sm font-medium rounded-lg whitespace-nowrap">
              {bulkSaving ? "Applying..." : "Apply to selected"}
            </button>
          </>
        }
      >
        <div className="space-y-4">
          <p className="text-sm text-slate-600 leading-relaxed">
            Apply the same quantity change to every selected line. Reductions that would take a location below zero are skipped automatically.
          </p>
          {bulkError && <div className="px-3 py-2.5 bg-red-50 border border-red-100 rounded-lg text-sm text-red-700">{bulkError}</div>}
          {bulkResult && <div className="px-3 py-2.5 bg-emerald-50 border border-emerald-100 rounded-lg text-sm text-emerald-700">{bulkResult}</div>}
          <div>
            <label className={labelCls}>Quantity change *</label>
            <input type="number" value={bulkQty} onChange={(e) => setBulkQty(e.target.value)} className={inputCls} placeholder="e.g. 10 or -5" />
            <p className="text-xs text-slate-400 mt-1">Use a negative number to reduce stock.</p>
          </div>
          <div>
            <label className={labelCls}>Reason</label>
            <input value={bulkReason} onChange={(e) => setBulkReason(e.target.value)} className={inputCls} placeholder="Cycle count, damage, etc." />
          </div>
        </div>
      </Modal>

      <OpeningInventoryModal
        open={openingOpen}
        onClose={() => setOpeningOpen(false)}
        orgId={org!.id}
        products={data?.products ?? []}
        variants={data?.variants ?? []}
        locations={data?.locations ?? []}
        onDone={() => {
          setOpeningOpen(false);
          queryClient.invalidateQueries({ queryKey: ["inventory", org?.id] });
          queryClient.invalidateQueries({ queryKey: ["dashboard", org?.id] });
        }}
      />
    </div>
  );
}
